var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect } from 'react';
import { translate } from '@utils/i18n';
import { Button } from '@ui/atoms/Button';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@ui/molecules/Modal';
const namespace = 'features.inspections_answer_by_type';
const REPORT_LANGUAGE_TO_CODE = {
    english: 'EN',
    french: 'FR',
    spanish: 'ES',
    en: 'EN',
    fr: 'FR',
    es: 'ES'
};
/** Map tenant default (english/french or EN/FR) to a value present in languageCodes. */
export function resolveDefaultLanguageCode(defaultLanguage, languageCodes) {
    var _a;
    if (languageCodes.includes(defaultLanguage))
        return defaultLanguage;
    const upper = defaultLanguage.toUpperCase();
    if (languageCodes.includes(upper))
        return upper;
    const mapped = REPORT_LANGUAGE_TO_CODE[defaultLanguage.toLowerCase()];
    if (mapped && languageCodes.includes(mapped))
        return mapped;
    return (_a = languageCodes[0]) !== null && _a !== void 0 ? _a : 'EN';
}
function buildJobs(reports, language, exclude, showGroupBySection, groupBySection) {
    return reports.map((r) => {
        if (!r.url)
            return { job: r, status: 'error', error: translate('bulk_modal.error_no_url', { namespace }) };
        let url = r.url;
        url += (url.includes('?') ? '&' : '?') + `language=${encodeURIComponent(language)}`;
        if (exclude)
            url += `&exclude_untested_extinguishers=true`;
        if (showGroupBySection) {
            url += `&group_alarm_devices_by_section=${groupBySection ? '1' : '0'}`;
        }
        return { job: Object.assign(Object.assign({}, r), { url }), status: 'pending' };
    });
}
export default function BulkReportModal({ reports, languageCodes, defaultLanguage, showExcludeUntested, showGroupBySection = false, groupBySectionDefault = false, onClose }) {
    const resolvedDefaultLanguage = resolveDefaultLanguageCode(defaultLanguage, languageCodes);
    const needsOptions = languageCodes.length > 1 || showExcludeUntested || showGroupBySection;
    const [phase, setPhase] = useState(needsOptions ? 'options' : 'progress');
    const [selectedLanguage, setSelectedLanguage] = useState(resolvedDefaultLanguage);
    const [excludeUntested, setExcludeUntested] = useState(false);
    const [groupBySection, setGroupBySection] = useState(groupBySectionDefault);
    const [jobs, setJobs] = useState([]);
    const [started, setStarted] = useState(false);
    const startGeneration = (language, exclude, groupSection) => {
        setJobs(buildJobs(reports, language, exclude, showGroupBySection, groupSection));
        setPhase('progress');
        setStarted(true);
    };
    useEffect(() => {
        if (!needsOptions && !started) {
            startGeneration(resolvedDefaultLanguage, false, false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!started || phase !== 'progress' || jobs.length === 0)
            return;
        let cancelled = false;
        function run() {
            return __awaiter(this, void 0, void 0, function* () {
                for (let i = 0; i < jobs.length; i++) {
                    if (cancelled)
                        break;
                    if (jobs[i].status !== 'pending')
                        continue;
                    setJobs((prev) => prev.map((j, idx) => (idx === i ? Object.assign(Object.assign({}, j), { status: 'running' }) : j)));
                    try {
                        const resp = yield fetch(jobs[i].job.url, { method: 'GET' });
                        if (!resp.ok)
                            throw new Error(`HTTP ${resp.status}`);
                        setJobs((prev) => prev.map((j, idx) => (idx === i ? Object.assign(Object.assign({}, j), { status: 'done' }) : j)));
                    }
                    catch (e) {
                        const message = e instanceof Error ? e.message : 'Unknown error';
                        setJobs((prev) => prev.map((j, idx) => (idx === i ? Object.assign(Object.assign({}, j), { status: 'error', error: message }) : j)));
                    }
                    yield new Promise((res) => setTimeout(res, 500));
                }
            });
        }
        void run();
        return () => {
            cancelled = true;
        };
    }, [started]); // eslint-disable-line react-hooks/exhaustive-deps
    const completed = jobs.filter((j) => j.status === 'done' || j.status === 'error').length;
    const total = jobs.length;
    const allDone = total > 0 && completed === total;
    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
    const titleId = 'bulk-report-modal-title';
    const title = phase === 'options'
        ? reports.length === 1
            ? translate('bulk_modal.options_title_one', { namespace, count: reports.length })
            : translate('bulk_modal.options_title_other', { namespace, count: reports.length })
        : translate('bulk_modal.progress_title', { namespace });
    return (React.createElement(Modal, { isOpen: true, onClose: phase === 'options' ? onClose : () => { }, dismissOnBackdropClick: phase === 'options', ariaLabelledBy: titleId },
        React.createElement(ModalHeader, { title: title, titleId: titleId, onClose: phase === 'options' ? onClose : undefined }),
        React.createElement(ModalBody, null,
            phase === 'options' && (React.createElement("div", { className: "bulk-report-modal__options" },
                languageCodes.length > 1 && (React.createElement("div", { className: "bulk-report-modal__field" },
                    React.createElement("label", { className: "bulk-report-modal__label", htmlFor: "bulk-report-language" }, translate('bulk_modal.language_label', { namespace })),
                    React.createElement("select", { id: "bulk-report-language", className: "bulk-report-modal__select", value: selectedLanguage, onChange: (e) => setSelectedLanguage(e.target.value) }, languageCodes.map((code) => (React.createElement("option", { key: code, value: code }, translate(`bulk_modal.language_${code.toLowerCase()}`, { namespace }))))))),
                showExcludeUntested && (React.createElement("div", { className: "bulk-report-modal__checkbox-row" },
                    React.createElement("input", { type: "checkbox", id: "bulk-exclude-untested", className: "qmb-cbx", checked: excludeUntested, onChange: (e) => setExcludeUntested(e.target.checked) }),
                    React.createElement("label", { htmlFor: "bulk-exclude-untested", className: "bulk-report-modal__checkbox-label" }, translate('bulk_modal.exclude_untested', { namespace })))),
                showGroupBySection && (React.createElement("div", { className: "bulk-report-modal__checkbox-row" },
                    React.createElement("input", { type: "checkbox", id: "bulk-group-by-section", className: "qmb-cbx", checked: groupBySection, onChange: (e) => setGroupBySection(e.target.checked) }),
                    React.createElement("label", { htmlFor: "bulk-group-by-section", className: "bulk-report-modal__checkbox-label" }, translate('bulk_modal.group_by_section', { namespace })))))),
            phase === 'progress' && (React.createElement("div", { className: "bulk-report-modal__progress" },
                React.createElement("div", { className: "bulk-report-modal__progress-bar" },
                    React.createElement("div", { className: `bulk-report-modal__progress-fill${allDone ? ' is-done' : ''}`, style: { width: `${pct}%` } })),
                React.createElement("p", { className: "bulk-report-modal__progress-label" }, translate('bulk_modal.progress', { namespace, completed, total })),
                React.createElement("div", { className: "bulk-report-modal__job-list" }, jobs.map((j, i) => (React.createElement("div", { key: i, className: "bulk-report-modal__job" },
                    j.status === 'pending' && React.createElement("i", { className: "fa-regular fa-clock", "aria-hidden": "true" }),
                    j.status === 'running' && React.createElement("i", { className: "fa-solid fa-spinner fa-spin", "aria-hidden": "true" }),
                    j.status === 'done' && React.createElement("i", { className: "fa-solid fa-check", "aria-hidden": "true" }),
                    j.status === 'error' && React.createElement("i", { className: "fa-solid fa-xmark", "aria-hidden": "true" }),
                    React.createElement("span", { className: `bulk-report-modal__job-name${j.status === 'error' ? ' is-error' : ''}` },
                        j.job.name,
                        j.status === 'error' && j.error && (React.createElement("span", { className: "bulk-report-modal__job-error" },
                            " \u2014 ",
                            j.error)))))))))),
        React.createElement(ModalFooter, null,
            phase === 'options' && (React.createElement(React.Fragment, null,
                React.createElement(Button, { variant: "secondary", onClick: onClose }, translate('bulk_modal.cancel', { namespace })),
                React.createElement(Button, { variant: "primary", onClick: () => startGeneration(selectedLanguage, excludeUntested, groupBySection) },
                    React.createElement("i", { className: "fa-solid fa-arrows-rotate", "aria-hidden": "true" }),
                    reports.length === 1
                        ? translate('bulk_modal.generate_one', { namespace, count: reports.length })
                        : translate('bulk_modal.generate_other', { namespace, count: reports.length })))),
            phase === 'progress' && (React.createElement(Button, { variant: "secondary", disabled: !allDone, onClick: () => {
                    onClose();
                    window.location.reload();
                } }, allDone
                ? translate('bulk_modal.close_refresh', { namespace })
                : translate('bulk_modal.generating', { namespace }))))));
}
